// @djsem/ui — AI Cue Director screen model (Prompt 97 §12). Pure, testable derivations for the
// Electron Cue Director screen: timeline positioning, table rows, confidence/safety filtering,
// batch-approve eligibility, and safe-language labels. No hardware, no model calls.

/** Lifecycle state of an AI-suggested lighting cue, from suggestion through export/archive. */
export type CueApprovalStatus = "suggested" | "approved" | "rejected" | "edited" | "exported" | "archived";

/** An AI-suggested lighting cue for a track, with confidence and operator approval state. */
export interface CueDirectorSuggestion {
  id: string;
  cueType: string;
  intent: string;
  label: string;
  description: string;
  startTimeMs?: number;
  bar?: number;
  beat?: number;
  confidence: number;
  suggestedIntensity: number;
  suggestedSceneId?: string;
  approvalStatus: CueApprovalStatus;
  safetyWarnings: string[];
}

const SAFETY_SENSITIVE = new Set(["strobe_suggestion", "blackout_suggestion", "freeze_suggestion"]);

/**
 * Confidence bucket → colour token name (UI maps to CSS).
 * @param confidence - Suggestion confidence, 0..1.
 * @returns `"high"` (>=0.75), `"medium"` (>=0.4), or `"low"`.
 */
export function confidenceBand(confidence: number): "high" | "medium" | "low" {
  if (confidence >= 0.75) return "high";
  if (confidence >= 0.4) return "medium";
  return "low";
}

/**
 * A suggestion needs explicit human confirmation before export.
 * @param s - The cue type + confidence to check.
 * @returns True if the cue is safety-sensitive or below the 0.4 confidence threshold.
 */
export function needsConfirmation(s: Pick<CueDirectorSuggestion, "cueType" | "confidence">): boolean {
  return SAFETY_SENSITIVE.has(s.cueType) || s.confidence < 0.4;
}

/**
 * Timeline placement (0..1 across the track) for a suggestion.
 * @param s - The suggestion's start time.
 * @param durationMs - Total track duration in ms.
 * @returns A value clamped to 0..1; 0 if duration/start time are missing or invalid.
 */
export function timelinePosition(s: Pick<CueDirectorSuggestion, "startTimeMs">, durationMs: number): number {
  if (!durationMs || durationMs <= 0 || s.startTimeMs == null) return 0;
  const p = s.startTimeMs / durationMs;
  return p < 0 ? 0 : p > 1 ? 1 : p;
}

/** Table-view filter criteria for cue suggestions. */
export interface CueFilter { minConfidence?: number; status?: CueApprovalStatus; hideUnsafe?: boolean; }

/**
 * Filter suggestions for the table view.
 * @param items - Suggestions to filter.
 * @param filter - Confidence/status/safety criteria.
 * @returns The matching subset of `items`.
 */
export function filterSuggestions(items: CueDirectorSuggestion[], filter: CueFilter): CueDirectorSuggestion[] {
  return items.filter((s) => {
    if (filter.minConfidence != null && s.confidence < filter.minConfidence) return false;
    if (filter.status && s.approvalStatus !== filter.status) return false;
    if (filter.hideUnsafe && SAFETY_SENSITIVE.has(s.cueType)) return false;
    return true;
  });
}

/**
 * Which suggestions are eligible for batch-approve (safe, confident, not already decided).
 * @param items - Suggestions to check.
 * @param minConfidence - Minimum confidence required for batch-approval.
 * @returns The eligible subset of `items`.
 */
export function batchApproveEligible(items: CueDirectorSuggestion[], minConfidence: number): CueDirectorSuggestion[] {
  return items.filter((s) => s.approvalStatus === "suggested" && !SAFETY_SENSITIVE.has(s.cueType) && s.confidence >= minConfidence);
}

/**
 * Only approved/edited cues can be exported.
 * @param items - Suggestions to count.
 * @returns The number of `items` in `"approved"` or `"edited"` status.
 */
export function exportableCount(items: CueDirectorSuggestion[]): number {
  return items.filter((s) => s.approvalStatus === "approved" || s.approvalStatus === "edited").length;
}

/** A display-ready row for the cue suggestion table (formatted strings, no raw numerics). */
export interface CueTableRow {
  id: string; time: string; cueType: string; intent: string; description: string;
  confidence: string; confidenceBand: "high" | "medium" | "low"; intensity: string;
  scene: string; safety: string; status: CueApprovalStatus; needsConfirmation: boolean;
}

function fmtTime(ms?: number): string {
  if (ms == null) return "—";
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Build display rows for the suggestion table.
 * @param items - Suggestions to format.
 * @returns One {@link CueTableRow} per suggestion, in the same order.
 */
export function toTableRows(items: CueDirectorSuggestion[]): CueTableRow[] {
  return items.map((s) => ({
    id: s.id, time: fmtTime(s.startTimeMs), cueType: s.cueType, intent: s.intent, description: s.description,
    confidence: `${Math.round(s.confidence * 100)}%`, confidenceBand: confidenceBand(s.confidence),
    intensity: `${Math.round(s.suggestedIntensity * 100)}%`, scene: s.suggestedSceneId ?? "—",
    safety: s.safetyWarnings.length ? s.safetyWarnings[0] : "—", status: s.approvalStatus,
    needsConfirmation: needsConfirmation(s),
  }));
}

/**
 * Safe-language guard (Prompt 97 §5 / Story 97.6.2): the UI must never claim certified safety.
 * Returns the approved phrasing; callers use this for any status/label copy.
 */
export const CUE_SAFE_LANGUAGE = {
  heading: "AI-suggested lighting cues",
  disclaimer: "AI suggestions are operator-reviewed. This is simulation/dry-run only — not certified safe, no real hardware output.",
  strobeWarning: "Strobe cue — requires venue and operator review.",
  blackoutWarning: "Blackout cue — requires manual operator confirmation.",
} as const;

/**
 * Scan a piece of UI copy for forbidden safety claims (used by a UI test / release gate).
 * @param text - Copy to scan.
 * @returns True if `text` contains an unsafe safety claim not negated by a preceding "not/never/no/…".
 */
export function containsUnsafeClaim(text: string): boolean {
  const banned = [
    "certified safe", "certified laser safety", "automatic venue-safe", "no operator required",
    "hardware safety verified", "legal lighting compliance",
  ];
  const lower = text.toLowerCase();
  return banned.some((b) => {
    let from = 0;
    while (true) {
      const at = lower.indexOf(b, from);
      if (at === -1) return false;
      // Ignore negated forms ("not certified safe", "never certified safe", "isn't certified safe").
      const preceding = lower.slice(Math.max(0, at - 12), at);
      if (!/\b(not|never|no|isn't|aren't|without)\b[^.]*$/.test(preceding)) return true;
      from = at + b.length;
    }
  });
}
